import { useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { editorFullName } from '../../utils/editorName';
import type {
  ConceptualCategory,
  ConceptualCategoryRef,
  ConceptualLastEdit,
} from '../../types/conceptualProblems';

/**
 * Re-file a problem without leaving it (feature 029, FR-026 to FR-031).
 *
 * <p><b>An overlay, not a panel.</b> This route is viewport-locked and the frame's
 * "only the auction and the answer may scroll" contract is measured by the `@layout` battery, so a
 * picker rendered INSIDE the frame would add a third scroller and fail it. Portalled over the top
 * costs the frame nothing and is what makes SC-009 hold by construction.</p>
 *
 * <p>Portalled to `document.body` by `createPortal` below. Until 2026-09-15 this paragraph said so
 * and the code did not: the picker mounted inside the page tree, under `.problems-scroll`, and the
 * `@layout` battery flagged its category list as a third scroller the first time that battery ran
 * against a database the picker could open on.</p>
 *
 * <p><b>The shape follows the two nearest precedents rather than inventing one.</b> The backdrop,
 * the dialog chrome, Escape-to-dismiss and click-outside are the shared `DetailsPopup` idiom this
 * page's ⓘ already uses; the name field with its `isSaving` / `error` / Save / Cancel is the
 * `AskJeffNoteDialog` editing idiom. What is genuinely new is the focus trap — see
 * `useFocusTrap`, which this is the first caller of.</p>
 *
 * <p><b>Ticks are local until Save.</b> Cancel, Escape, clicking outside and stepping to another
 * problem all discard them (FR-029). Nothing is written until the editor says so, and what is
 * written is the WHOLE set rather than a change to it.</p>
 *
 * <p>"Uncategorised" is not a tickable entry. It is not a category — it is what the site calls a
 * problem with no categories at all — so it is reached by ticking nothing, which the warning below
 * makes explicit rather than leaving the editor to infer.</p>
 *
 * <p><b>The problem's own categories come first</b> (FR-027, amended 2026-09-15). In one flat list
 * the ticked rows were hard to find, so they are grouped at the top under a heading, with every
 * other category below a divider. The groups are fixed at the moment the picker opens and do not
 * follow the ticks: a row that moved on click would jump from under the pointer, and unticking
 * everything would become a chase. Where either group would be empty the list is drawn plain,
 * because a heading with nothing to tell apart is only noise.</p>
 *
 * <p><b>Read-only when a Super Editor has locked the problem</b> (FR-048, 2026-09-17). For anyone
 * but a Super Editor the same dialog opens with its ticks shown and disabled, no "New category" and
 * no Save — only Close — under a notice naming who locked it. Opening it rather than disabling the
 * masthead button is deliberate: a disabled icon on a phone cannot say why. A Super Editor gets the
 * normal picker with a one-line reminder that the problem is locked.</p>
 */
export function ConceptualCategoryPicker({
  categories,
  selected,
  isSaving,
  error,
  onSave,
  onCancel,
  locked = false,
  lastEdit = null,
  canEditLocked = false,
}: {
  /** Every category, in list order. The Uncategorised bucket is filtered out here, not by the caller. */
  categories: ConceptualCategory[];
  /** The problem's categories as they stand. */
  selected: ConceptualCategoryRef[];
  isSaving: boolean;
  /** A failed save, kept on screen with the ticks intact so the editor can retry or amend. */
  error: string | null;
  onSave: (categoryIds: number[], newCategoryName: string | null) => void;
  onCancel: () => void;
  /** A Super Editor has saved this problem (FR-048). */
  locked?: boolean;
  /** Who saved it last — on a locked problem, the Super Editor who locked it. */
  lastEdit?: ConceptualLastEdit | null;
  /** The viewer is a Super Editor, who may still edit a locked problem. */
  canEditLocked?: boolean;
}) {
  const containerRef = useFocusTrap<HTMLDivElement>(true);
  const readOnly = locked && !canEditLocked;

  // Mounted only while open — every caller wraps it in its own `{isOpen && …}` — so the hook is
  // unconditionally active, the same reasoning DetailsPopup records for its own Escape handling.
  useEscapeClose(() => { if (!isSaving) onCancel(); }, true);

  const [ticked, setTicked] = useState<Set<number>>(
    () => new Set(selected.map((c) => c.id)),
  );
  const [newName, setNewName] = useState('');
  const [addingNew, setAddingNew] = useState(false);

  // What the problem was filed under when the picker OPENED — deliberately not `ticked`, so the
  // grouping below never reacts to a click. Captured once, the same way `ticked` is seeded.
  const [assignedAtOpen] = useState<ReadonlySet<number>>(
    () => new Set(selected.map((c) => c.id)),
  );
  const assignedHeadingId = useId();
  const otherHeadingId = useId();

  // The synthetic bucket never appears as a choice: it has no id to save, and ticking it would
  // mean something different from every other row in the list.
  const tickable = useMemo(
    () => categories.filter((c): c is ConceptualCategory & { id: number } => c.id !== null),
    [categories],
  );

  // A stable partition, so the category list's order still holds WITHIN each group (FR-027).
  const assigned = tickable.filter((c) => assignedAtOpen.has(c.id));
  const unassigned = tickable.filter((c) => !assignedAtOpen.has(c.id));
  const grouped = assigned.length > 0 && unassigned.length > 0;

  const toggle = (id: number) => {
    setTicked((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // One row, whichever group it is drawn in. The checkbox stays INSIDE its label: that is its
  // accessible name, and the E2E suite climbs `closest('label')` to read it.
  const renderRow = (category: ConceptualCategory & { id: number }) => (
    <li key={category.id}>
      <label className="flex min-h-[24px] cursor-pointer items-center gap-2 rounded px-1 py-1.5 hover:bg-paper [@media(max-height:500px)]:py-1">
        <input
          type="checkbox"
          className="h-4 w-4 shrink-0"
          checked={ticked.has(category.id)}
          onChange={() => toggle(category.id)}
          disabled={isSaving || readOnly}
        />
        <span className="min-w-0 text-sm text-ink">{category.name}</span>
      </label>
    </li>
  );

  const trimmedName = newName.trim();

  // FR-031: saving with nothing ticked is allowed and is sometimes what the editor means, so this
  // is a statement of the consequence rather than a block. Suppressed while a new name is being
  // typed, since that name will become the problem's one category.
  const showsEmptyWarning = !readOnly && ticked.size === 0 && trimmedName.length === 0;

  const lockerName = lastEdit ? editorFullName(lastEdit) : null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={() => { if (!isSaving) onCancel(); }}
      data-testid="conceptual-category-picker-overlay"
    >
      {/* A centred popup at every width, with a gutter on phones — the shape AddCategoryDialog
          already has. Until 2026-09-15 it was a sheet pinned to the bottom edge below md, which on
          a phone read as the page's footer rather than a popup (client). */}
      {/* A column in which ONLY THE LIST gives way (client, 2026-09-17, from a 764 × 324 and an
          814 × 430 landscape phone). Until then the whole dialog was the scroller and the list
          had a fixed max-h-64: on a 324px-tall screen 85vh is ~275px, the list alone took 256 of
          them, and Save / Cancel / Close sat below the fold behind a second scroller. Now the
          dialog never scrolls: the title, the lock notice, "New category" and the buttons keep
          their height (`shrink-0`), and the list — `min-h-0`, still capped at max-h-64 on a tall
          screen — shrinks to whatever is left and scrolls inside itself. `dvh`, not `vh`, so a
          mobile browser's toolbar is not counted as room the dialog can use; on a short landscape
          screen the cap rises to 94dvh and the padding tightens, because every pixel there is
          either list or nothing. */}
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Categories for this problem"
        className="flex max-h-[85dvh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-card p-4 shadow-xl [@media(max-height:500px)]:max-h-[94dvh] [@media(max-height:500px)]:p-3"
        onClick={(e) => e.stopPropagation()}
        data-testid="conceptual-category-picker"
      >
        <h2 className="mb-3 shrink-0 text-base font-semibold text-ink [@media(max-height:500px)]:mb-2">
          Categories for this problem
        </h2>

        {/* The lock notice, on the theme's brass-edged notice surface (FR-048). */}
        {locked && (
          <p
            className="mb-3 flex shrink-0 items-start gap-2 rounded-md border border-brass bg-notice px-3 py-2 text-sm text-notice-ink [@media(max-height:500px)]:mb-2 [@media(max-height:500px)]:py-1.5"
            data-testid="conceptual-picker-locked"
          >
            <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0 text-brass-text" aria-hidden="true" />
            <span>
              {readOnly
                ? `Locked by Super Editor${lockerName ? ` ${lockerName}` : ''}. Only a Super Editor can change these categories.`
                : 'This problem is locked. Only Super Editors can change its categories.'}
            </span>
          </p>
        )}

        {/* The largest live category holds 212 problems but there are only eleven categories, so
            this list is short today. It scrolls anyway: FR-004 says the page must take any number
            of categories without a code change, and that has to include a number that does not
            fit. The scroller is the wrapper, so both groups scroll as one. It is the ONLY thing in
            the dialog allowed to shrink (see the dialog's comment); `min-h-[4.5rem]` keeps about two
            rows in view on the shortest supported screen, so it never collapses to nothing. */}
        <div
          className="mb-3 max-h-64 min-h-[4.5rem] overflow-y-auto overscroll-contain [@media(max-height:500px)]:mb-2"
          data-testid="conceptual-category-picker-list"
        >
          {grouped ? (
            <>
              {/* Sentence case and muted, after ChapterPicker's grouped popover — the nearest
                  precedent for a short list split into labelled groups. */}
              <h3 id={assignedHeadingId} className="px-1 pb-1 text-xs font-semibold text-ink-soft">
                Assigned
              </h3>
              <ul aria-labelledby={assignedHeadingId} className="space-y-1">
                {assigned.map(renderRow)}
              </ul>

              <div className="mt-2 border-t border-line pt-2">
                <h3 id={otherHeadingId} className="px-1 pb-1 text-xs font-semibold text-ink-soft">
                  Other categories
                </h3>
                <ul aria-labelledby={otherHeadingId} className="space-y-1">
                  {unassigned.map(renderRow)}
                </ul>
              </div>
            </>
          ) : (
            <ul className="space-y-1">{tickable.map(renderRow)}</ul>
          )}
        </div>

        {readOnly ? null : addingNew ? (
          <div className="mb-3 shrink-0 [@media(max-height:500px)]:mb-2">
            <label htmlFor="conceptual-new-category" className="mb-1 block text-sm text-ink-soft">
              New category
            </label>
            <input
              id="conceptual-new-category"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={100}
              disabled={isSaving}
              autoFocus
              className="w-full rounded border border-line px-2 py-1.5 text-sm text-ink"
              data-testid="conceptual-new-category-input"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAddingNew(true)}
            disabled={isSaving}
            className="mb-3 min-h-[24px] shrink-0 self-start text-sm font-medium text-primary-700 hover:underline [@media(max-height:500px)]:mb-2"
            data-testid="conceptual-add-category"
          >
            + New category
          </button>
        )}

        {showsEmptyWarning && (
          <p className="mb-3 shrink-0 text-sm text-ink-soft [@media(max-height:500px)]:mb-2" data-testid="conceptual-empty-warning">
            With nothing ticked, this problem will be filed under &ldquo;Uncategorised&rdquo;.
          </p>
        )}

        {/* A PROBLEM_LOCKED refusal turns the picker read-only, and the lock notice above already
            says why, so the server's sentence is not repeated beneath it. */}
        {error && !readOnly && (
          <p className="mb-3 shrink-0 text-sm text-danger [@media(max-height:500px)]:mb-2" role="alert" data-testid="conceptual-picker-error">
            {error}
          </p>
        )}

        <div className="flex shrink-0 justify-end gap-2">
          {readOnly ? (
            <button
              type="button"
              onClick={onCancel}
              className="min-h-[24px] rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              data-testid="conceptual-picker-close"
            >
              Close
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onCancel}
                disabled={isSaving}
                className="min-h-[24px] rounded-md px-4 py-2 text-sm font-medium text-ink hover:bg-paper disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => onSave([...ticked], trimmedName.length > 0 ? trimmedName : null)}
                disabled={isSaving}
                className="min-h-[24px] rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                data-testid="conceptual-picker-save"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
